import { CheckShippingItemsItem } from "services/interface";

/** บริษัทขนส่งสำหรับ dropdown "ส่งเอง" (map จาก /master/master_by_process/self_shipping_carrier) */
export interface SelfShippingBrand {
  id: string;
  uuid?: string;
  value?: string;
  brand_name: string;
  brand_logo?: string;
}

export const mapSelfShippingBrands = (res: any): SelfShippingBrand[] => {
  const list = res?.data || res || [];
  return (Array.isArray(list) ? list : []).map((item: any) => ({
    id: item.id,
    uuid: item.uuid,
    value: item.value,
    brand_name: item.description || item.value || "Unknown",
    brand_logo: item.image,
  }));
};

/** ค่า id ที่ส่งเป็น self_transport_brand_id */
export const getSelfShippingBrandKey = (b: SelfShippingBrand) => b.uuid || b.id;

/** FT (Flash Home / ส่งด้วยเบอร์โทร) ใช้เบอร์โทรแทนเลขพัสดุ */
export const isPhoneTrackingBrand = (brands: SelfShippingBrand[], brandKey?: string) =>
  !!brandKey && brands.find((b) => getSelfShippingBrandKey(b) === brandKey)?.value === "FT";

/** กรองอักขระแบบ real-time ระหว่างพิมพ์ — FT รับเฉพาะตัวเลข 10 หลัก, อื่น ๆ a-z A-Z 0-9 */
export const cleanSelfTrackingInput = (value: string, isPhone: boolean) =>
  isPhone ? value.replace(/[^0-9]/g, "").slice(0, 10) : value.replace(/[^a-zA-Z0-9]/g, "");

/** คืนข้อความ error ถ้าเลขไม่ผ่าน, null ถ้าผ่าน */
export const validateSelfTrackingNumber = (value: string, isPhone: boolean): string | null => {
  if (!value.trim()) {
    return isPhone ? "กรุณากรอกหมายเลขโทรศัพท์" : "กรุณากรอกหมายเลขพัสดุ";
  }
  if (isPhone) {
    if (!/^0[0-9]{8,9}$/.test(value)) {
      return "หมายเลขโทรศัพท์ต้องขึ้นต้นด้วยเลข 0 และมีความยาว 9-10 หลักเท่านั้น";
    }
    return null;
  }
  if (/\s/.test(value)) {
    return "หมายเลขพัสดุต้องไม่มีช่องว่าง";
  }
  if (/[^a-zA-Z0-9_]/.test(value)) {
    return "หมายเลขพัสดุต้องประกอบด้วยตัวอักษร ตัวเลข หรือขีดล่าง (_) เท่านั้น";
  }
  return null;
};

/**
 * แจกเลขพัสดุให้ทุกชิ้น (เรียงตามรายการสินค้า): เลขแรกใช้กับทุกชิ้น · เลขที่เพิ่ม (ตัวที่ 2..K)
 * ไล่ใส่ชิ้นท้ายสุดย้อนขึ้นมา ชิ้นละเลข — เช่น 3 ชิ้น 2 เลข [A,B] → [A,A,B] · 3 ชิ้น 3 เลข → [A,B,C]
 */
export const assignTrackingNumbersToPieces = (totalPieces: number, numbers: string[]): string[] => {
  const k = Math.min(Math.max(numbers.length, 1), Math.max(totalPieces, 1));
  const firstExtraPiece = totalPieces - (k - 1);
  return Array.from({ length: totalPieces }, (_, j) =>
    j >= firstExtraPiece ? numbers[j - firstExtraPiece + 1] ?? "" : numbers[0] ?? ""
  );
};

/**
 * ตัวกลับของ assignTrackingNumbersToPieces — แปลงเลขรายชิ้น (เรียงตาม item_no) กลับเป็นช่องในฟอร์ม
 * เช่น [A,A,B] → [A,B] · [A,A,A] → [A] · [A,B,C] → [A,B,C]
 */
export const piecesToTrackingNumbers = (pieces: string[]): string[] => {
  const lead = pieces.findIndex((p) => p !== pieces[0]);
  return lead === -1 ? pieces.slice(0, 1) : [pieces[0], ...pieces.slice(lead)];
};

/** ชิ้น "ส่งเอง" ที่ยังอยู่แท็บเตรียมจัดส่ง (factory_approve) = ยังกรอกเลขพัสดุจริงได้ */
export const getSelfPendingItems = (items: CheckShippingItemsItem[] = []) =>
  items.filter((i) => i.shipping_method === "self" && i.item_status === "factory_approve");

/** ทุกชิ้นที่ยังไม่ยกเลิกเป็น "ส่งเอง" — ไม่มีชิ้นไหนต้องเรียกขนส่งแบ่งเบา */
export const isSelfOnlyOrder = (items: CheckShippingItemsItem[] = []) => {
  const active = items.filter((i) => i.item_status !== "cancel");
  return active.length > 0 && active.every((i) => i.shipping_method === "self");
};

/**
 * ยังมีชิ้นที่ต้องออก Label อยู่ไหม — ชิ้นที่ยังเป็น factory_approve และยังไม่มีแถวพัสดุ (has_label)
 * ส่งเองที่ออก Label แล้วได้เลขอ้างอิง BB แต่ยังอยู่ factory_approve จนกว่าจะกรอกเลขจริง → ดูจาก has_label
 * backend ที่ยังไม่ส่ง items → ถือว่ายังต้องออก (ไม่ตัดออก)
 */
export const hasItemsAwaitingLabel = (s?: ShipItemsSummary) =>
  !s?.items || s.items.some((i) => i.item_status === "factory_approve" && !i.has_label);

/** ผลของ checkShippingItems ต่อออเดอร์ (y = ชิ้นส่งแบ่งเบา, z = ชิ้นส่งเอง) */
export interface ShipItemsSummary {
  y: number;
  z: number;
  items?: CheckShippingItemsItem[];
}

export interface SelfTrackingState {
  pending: boolean;
  selfOnly: boolean;
  /** null = backend ไม่ได้ส่ง items มา → ให้ modal ใช้ทุกรายการ factory_approve ของออเดอร์ (ส่งเองล้วน) */
  pendingItems: CheckShippingItemsItem[] | null;
}

export const getSelfTrackingState = (s?: ShipItemsSummary): SelfTrackingState | undefined => {
  if (!s) return undefined;
  if (s.items) {
    const pendingItems = getSelfPendingItems(s.items);
    return { pending: pendingItems.length > 0, selfOnly: isSelfOnlyOrder(s.items), pendingItems };
  }
  // backend ที่ยังไม่ส่ง items (ก่อน deploy) — รองรับเฉพาะออเดอร์ส่งเองล้วน: ทุกชิ้นที่ยังอยู่แท็บนี้เป็นส่งเอง
  const selfOnly = s.y === 0 && s.z > 0;
  return { pending: selfOnly, selfOnly, pendingItems: null };
};
